import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  CreditCard,
  LogIn,
  AlertCircle,
  ShieldAlert,
  Clock,
  AlertTriangle,
  Mail,
  MessageSquare,
  ArrowLeft,
  Send,
} from 'lucide-react';
import OtpVerificationForm from './OtpVerificationForm';
import { Input, Button } from '../../shared/components/ui';
import { ApiClientError } from '../../services/api';

/**
 * Login attempt status from the API
 */
export interface LoginAttemptStatus {
  isLocked: boolean;
  attemptCount: number;
  maxAttempts: number;
  remainingAttempts: number;
  lockRemainingSeconds: number;
  lockRemainingMinutes: number;
  lockedUntil: string | null;
}

/**
 * OTP delivery method
 */
export type OtpMethod = 'email' | 'sms';

/**
 * Result of credentials validation (Step 1)
 */
export interface CredentialsResult {
  success: boolean;
  credentialsValid?: boolean;
  authenticated?: boolean;
  otpBypassed?: boolean;
  availableOtpMethods?: OtpMethod[];
  maskedEmail?: string;
  maskedPhone?: string;
  loginStatus?: LoginAttemptStatus;
  error?: string;
}

/**
 * Result of sending OTP (Step 2)
 */
export interface SendOtpResult {
  success: boolean;
  requiresOtp?: boolean;
  otpSentTo?: string;
  otpExpiresIn?: number;
  deliveryMethod?: OtpMethod;
  error?: string;
  isLocked?: boolean;
  lockRemainingSeconds?: number;
}

/**
 * Result of OTP verification (Step 3)
 */
export interface OtpVerifyResult {
  success: boolean;
  error?: string;
  remainingAttempts?: number;
  isLocked?: boolean;
  lockRemainingSeconds?: number;
}

interface CourierLoginFormProps {
  /** Step 1: Validate credentials (phone + ID number) */
  onSubmitCredentials: (username: string, idNumber: string) => Promise<CredentialsResult>;
  /** Step 2: Send OTP via selected method */
  onSendOtp: (username: string, idNumber: string, method: OtpMethod) => Promise<SendOtpResult>;
  /** Step 3: Verify OTP code */
  onVerifyOtp: (username: string, idNumber: string, code: string) => Promise<OtpVerifyResult>;
  /** Resend OTP code */
  onResendOtp: (username: string, idNumber: string, method: OtpMethod) => Promise<SendOtpResult>;
}

/**
 * Courier Login Form Component
 * 
 * Three-step authentication:
 * 1. Phone number and ID number (תעודת זהות) validation
 * 2. Choose OTP delivery method (Email or SMS)
 * 3. OTP verification
 * 
 * Includes security features:
 * - Failed attempt tracking
 * - Account lockout warning
 * - OTP expiration timer
 */
const CourierLoginForm: React.FC<CourierLoginFormProps> = ({
  onSubmitCredentials,
  onSendOtp,
  onVerifyOtp,
  onResendOtp,
}) => {
  // Current step: 'credentials' | 'method-selection' | 'otp-verification'
  const [currentStep, setCurrentStep] = useState<'credentials' | 'method-selection' | 'otp-verification'>('credentials');

  // Form state
  const [username, setUsername] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginStatus, setLoginStatus] = useState<LoginAttemptStatus | null>(null);
  const [lockCountdown, setLockCountdown] = useState(0);

  // OTP method selection state
  const [availableOtpMethods, setAvailableOtpMethods] = useState<OtpMethod[]>([]);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<OtpMethod | null>(null);

  // OTP state
  const [otpSentTo, setOtpSentTo] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(180);
  const [deliveryMethod, setDeliveryMethod] = useState<OtpMethod>('email');

  // Countdown timer for locked accounts
  useEffect(() => {
    if (lockCountdown <= 0) return;

    const timer = setInterval(() => {
      setLockCountdown((prev) => {
        if (prev <= 1) {
          setLoginStatus(null);
          setError(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [lockCountdown]);

  // Update countdown when login status changes
  useEffect(() => {
    if (loginStatus?.isLocked && loginStatus.lockRemainingSeconds > 0) {
      setLockCountdown(loginStatus.lockRemainingSeconds);
    }
  }, [loginStatus]);

  // Step 1: Submit credentials (phone + ID number)
  const handleSubmitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (!idNumber.trim()) {
      setError('Please enter your ID number');
      return;
    }

    if (loginStatus?.isLocked && lockCountdown > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await onSubmitCredentials(username.trim(), idNumber.trim());

      if (result.loginStatus) {
        setLoginStatus(result.loginStatus);
      }

      if (result.success || result.credentialsValid) {
        if (result.authenticated && result.otpBypassed) {
          // Auth context has already set the user; login page redirect effect will navigate.
          return;
        }

        // Credentials valid! Move to method selection
        setAvailableOtpMethods(result.availableOtpMethods || []);
        setMaskedEmail(result.maskedEmail || '');
        setMaskedPhone(result.maskedPhone || '');

        // Pre-select SMS as default method if available, otherwise email
        if (result.availableOtpMethods && result.availableOtpMethods.length > 0) {
          if (result.availableOtpMethods.includes('sms')) {
            setSelectedMethod('sms');
          } else {
            setSelectedMethod(result.availableOtpMethods[0]);
          }
        }

        setCurrentStep('method-selection');
      } else if (!result.success) {
        setError(result.error || 'Invalid phone number or ID number');
      }
    } catch (err) {
      const apiErr = err as ApiClientError;
      const loginStatusFromError = apiErr.response?.data?.loginStatus;
      if (loginStatusFromError) {
        setLoginStatus(loginStatusFromError as LoginAttemptStatus);
      }
      setError(apiErr.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Send OTP via selected method
  const handleSendOtp = async () => {
    if (!selectedMethod) {
      setError('Please select how you want to receive the verification code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await onSendOtp(username, idNumber, selectedMethod);

      if (result.success) {
        setOtpSentTo(result.otpSentTo || '');
        setOtpExpiresIn(result.otpExpiresIn || 180);
        setDeliveryMethod(result.deliveryMethod || selectedMethod);
        setCurrentStep('otp-verification');
      } else {
        setError(result.error || 'Failed to send verification code');
      }
    } catch (err) {
      const apiErr = err as ApiClientError;
      setError(apiErr.response?.data?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Verify OTP
  const handleVerifyOtp = async (code: string): Promise<OtpVerifyResult> => {
    return onVerifyOtp(username, idNumber, code);
  };

  // Resend OTP
  const handleResendOtp = async (): Promise<{ success: boolean; expiresIn?: number; error?: string; isLocked?: boolean; lockRemainingSeconds?: number }> => {
    const result = await onResendOtp(username, idNumber, deliveryMethod);
    if (result.success) {
      setOtpSentTo(result.otpSentTo || otpSentTo);
    }
    return {
      success: result.success,
      expiresIn: result.otpExpiresIn,
      error: result.error,
      isLocked: result.isLocked,
      lockRemainingSeconds: result.lockRemainingSeconds,
    };
  };

  // Go back to credentials step
  const handleBackToCredentials = () => {
    setCurrentStep('credentials');
    setIdNumber('');
    setError(null);
    setSelectedMethod(null);
  };

  // Go back to method selection
  const handleBackToMethodSelection = () => {
    setCurrentStep('method-selection');
    setError(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isLocked = loginStatus?.isLocked && lockCountdown > 0;
  const hasWarning = loginStatus && !loginStatus.isLocked && loginStatus.attemptCount > 0;

  // Step 3: OTP Verification
  if (currentStep === 'otp-verification') {
    return (
      <OtpVerificationForm
        username={username}
        otpSentTo={otpSentTo}
        initialExpiresIn={otpExpiresIn}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        onBack={handleBackToMethodSelection}
      />
    );
  }

  // Step 2: OTP Method Selection
  if (currentStep === 'method-selection') {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
        className="space-y-4"
      >
        {/* Back Button */}
        <button
          type="button"
          onClick={handleBackToCredentials}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>

        {/* Header */}
        <div className="text-center py-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Choose Verification Method
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            How would you like to receive the verification code?
          </p>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-[#c00035] text-sm"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Method Options - SMS first as default */}
        <div className="space-y-3">
          {availableOtpMethods.includes('sms') && (
            <button
              type="button"
              onClick={() => setSelectedMethod('sms')}
              disabled={isLoading}
              className={`
                w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4
                disabled:opacity-60 disabled:cursor-not-allowed
                ${selectedMethod === 'sms'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className={`
                p-3 rounded-lg
                ${selectedMethod === 'sms' ? 'bg-primary-100' : 'bg-gray-100'}
              `}>
                <MessageSquare className={`w-6 h-6 ${selectedMethod === 'sms' ? 'text-primary-600' : 'text-gray-500'}`} />
              </div>
              <div className="flex-1 text-left">
                <p className={`font-semibold ${selectedMethod === 'sms' ? 'text-primary-700' : 'text-gray-700'}`}>
                  SMS
                </p>
                <p className="text-sm text-gray-500">
                  Send to {maskedPhone || 'your phone'}
                </p>
              </div>
              {selectedMethod === 'sms' && (
                <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          )}

          {availableOtpMethods.includes('email') && (
            <button
              type="button"
              onClick={() => setSelectedMethod('email')}
              disabled={isLoading}
              className={`
                w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4
                disabled:opacity-60 disabled:cursor-not-allowed
                ${selectedMethod === 'email'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className={`
                p-3 rounded-lg
                ${selectedMethod === 'email' ? 'bg-primary-100' : 'bg-gray-100'}
              `}>
                <Mail className={`w-6 h-6 ${selectedMethod === 'email' ? 'text-primary-600' : 'text-gray-500'}`} />
              </div>
              <div className="flex-1 text-left">
                <p className={`font-semibold ${selectedMethod === 'email' ? 'text-primary-700' : 'text-gray-700'}`}>
                  Email
                </p>
                <p className="text-sm text-gray-500">
                  Send to {maskedEmail || 'your email'}
                </p>
              </div>
              {selectedMethod === 'email' && (
                <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          )}
        </div>

        {/* Send Button */}
        <Button
          type="button"
          onClick={handleSendOtp}
          disabled={!selectedMethod}
          isLoading={isLoading}
          icon={<Send className="w-5 h-5" />}
          variant="primary"
          size="lg"
          className="w-full"
        >
          {isLoading ? 'Sending...' : 'Send Verification Code'}
        </Button>
      </motion.div>
    );
  }

  // Step 1: Credentials Form (Phone + ID Number)
  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      onSubmit={handleSubmitCredentials}
      className="space-y-4"
    >
      {/* Locked Account Message */}
      <AnimatePresence>
        {isLocked && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 bg-red-50 border border-red-200 rounded-xl"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-[#c00035]" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-red-800">Account Temporarily Locked</h4>
                <p className="text-sm text-[#c00035] mt-1">
                  Too many failed login attempts. Please wait before trying again.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Clock className="w-4 h-4 text-[#c00035]" />
                  <span className="text-lg font-mono font-bold text-[#c00035]">
                    {formatTime(lockCountdown)}
                  </span>
                  <span className="text-sm text-[#c00035]">remaining</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Warning for remaining attempts */}
      <AnimatePresence>
        {hasWarning && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm text-amber-800">
                {loginStatus.remainingAttempts === 1 ? (
                  <strong>Last attempt!</strong>
                ) : (
                  <>
                    <strong>{loginStatus.remainingAttempts}</strong> attempts remaining
                  </>
                )}{' '}
                before account is locked for 15 minutes.
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && !isLocked && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-[#c00035] text-sm"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phone Number Input */}
      <Input
        id="username"
        label="Phone Number"
        type="tel"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="Enter your phone number"
        disabled={isLoading || isLocked}
        startIcon={<Phone className="w-5 h-5" />}
        error={isLocked}
      />

      {/* ID Number Input */}
      <Input
        id="idNumber"
        label="ID Number (תעודת זהות)"
        type="text"
        value={idNumber}
        onChange={(e) => setIdNumber(e.target.value)}
        placeholder="Enter your ID number"
        disabled={isLoading || isLocked}
        startIcon={<CreditCard className="w-5 h-5" />}
        error={isLocked}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isLocked}
        isLoading={isLoading}
        icon={isLocked ? <ShieldAlert className="w-5 h-5" /> : <LogIn className="w-5 h-5" />}
        variant={isLocked ? 'secondary' : 'primary'}
        size="lg"
        className="w-full"
      >
        {isLoading ? 'Verifying...' : isLocked ? 'Account Locked' : 'Continue'}
      </Button>

      {/* Help Text */}
      <p className="text-xs text-gray-500 text-center">
        A verification code will be sent to your email or phone
      </p>
    </motion.form>
  );
};

export default CourierLoginForm;
